import { Button } from '@civilgrid/ui/components';
import { useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { newAgencyContact as apiNewAgencyContact } from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import ControlledCheckbox from '~/components2/forms/controlled/ControlledCheckbox';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import ControlledSelect from '~/components2/forms/controlled/ControlledSelect';
import ControlledTextArea from '~/components2/forms/controlled/ControlledTextArea';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import store from '../../store';
import { buildSubRegionItems, SUB_REGION_NONE, subRegionFkValue } from '../../subRegionOptions';

type TFormValues = {
  isDefaultContact: boolean;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  notes: string;
  agencySubGeometryId: string;
};

export default function NewContact() {
  const { agency, subGeometries } = useSnapshot(store) as typeof store;

  const form = useForm<TFormValues>({
    defaultValues: {
      isDefaultContact: false,
      firstName: '',
      lastName: '',
      title: '',
      email: '',
      notes: '',
      agencySubGeometryId: SUB_REGION_NONE,
    },
  });

  const subRegionItems = useMemo(
    () =>
      buildSubRegionItems(subGeometries).map(({ value, label }) => ({
        value,
        label: <div className="text-xs">{label}</div>,
      })),
    [subGeometries],
  );

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    const fk = subRegionFkValue(data.agencySubGeometryId);
    const properties = { ...data, agency: agency!.id, agencySubGeometryId: fk };

    try {
      const result = await apiNewAgencyContact(properties);

      Notify.info({
        title: 'New Agency Contact',
        message: 'Agency contact successfully created.',
      });

      if (fk) {
        capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberAssigned, {
          agency_id: agency!.id,
          sub_geometry_id: fk,
          member_type: 'contact',
          member_id: result.id,
          entry_point: 'contact_editor',
        });
      }

      form.reset({});

      store.fetchData(agency!.id);
    } catch {
      Notify.error({
        title: 'New Agency Contact',
        message: 'An error occurred adding the contact.',
      });
    }
  };

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="w-1/3 space-y-2">
        <Button type="submit" disabled={!form.formState.isDirty}>
          Save Contact
        </Button>

        <ControlledCheckbox label="Agency default contact" control={form.control} name="isDefaultContact" />

        <ControlledInput
          label="Agency Contact Email"
          control={form.control}
          name="email"
          isRequired={false}
          tooltipText="Agency contact email address"
          placeholder="Ex: john.smith@acme-engineering.com"
        />

        <ControlledInput
          label="Agency Contact First Name"
          control={form.control}
          name="firstName"
          isRequired={false}
          tooltipText="Agency contact first name"
          placeholder="Ex: John"
        />

        <ControlledInput
          label="Agency Contact Last Name"
          control={form.control}
          name="lastName"
          isRequired={false}
          tooltipText="Agency contact last name"
          placeholder="Ex: Smith"
        />

        <ControlledInput
          label="Agency Contact Title"
          control={form.control}
          name="title"
          isRequired={false}
          tooltipText="Agency contact title (i.e. Project Engineer, Manager, etc.)"
          placeholder="Ex: Project Manager"
        />

        <ControlledTextArea
          label="Agency Contact Notes"
          control={form.control}
          name="notes"
          isRequired={false}
          tooltipText="Notes for the agency contact"
          placeholder="Add notes here for this agency contact."
        />

        <ControlledSelect
          label="Sub Region"
          control={form.control}
          items={subRegionItems}
          name="agencySubGeometryId"
          isRequired
          tooltipText="Assign this contact to one of the agency's sub regions, or leave unscoped"
          data-testid="agency-sub-geometries-new-contact-sub-region-select"
        />
      </div>
    </Form>
  );
}
